import { formatDate, formatDayMonth, formatMonthName, t } from "@hakili/i18n";
import type { DealerDocumentView, DealerDocumentsAnswer } from "@hakili/contracts";
import { Button, Chip, GuardedButton } from "@hakili/ui";
import { AlertTriangle, Camera, Check, Clock, Lock, Upload } from "lucide-react";
import { type ReactElement, useCallback, useEffect, useRef, useState } from "react";

import { documentsStripOf, documentsViewOf } from "../session/dealer-documents.js";
import type { DoorResult } from "../session/useSession.js";
import { HakiliFace } from "./hakili-face.js";
import { ReadFailure } from "./read-failure.js";

/**
 * `DEALER-V2-B1` (design/dealer-v2 `086ba19f`, touch-up `618ad8c6`; `P1-DOS-077`; D-1031-16 and D-1031-19): **« Pièces demandées par la banque pour ce dossier ».**
 *
 * **The server's words, drawn as sent.** The bar is the served `summary` (`ready`, `expected`): nothing is counted here. `monthsSentence`, `readingSentence`, `helpText`, the condition's sentence and the rejection
 * reason are printed verbatim, and where the server sent none the row prints none. A month chip stands only where the server named months (`readMonths`, `missingMonths`, served after Hakili's reading), never
 * from the clock. The missing pièces come first and ONE primary « Prendre en photo » stands on the next missing pièce; « Choisir un fichier » is the secondary on each. The photo is the browser's camera capture
 * on the existing upload ticket (no new door). The capture-time, per-page verdict is booked after the freeze: no slot is drawn for it.
 *
 * `continueLabel` and the two handlers belong to the step this card sits in (the assisted file's « Pièces »).
 */
const fillWith = (key: string, values: Readonly<Record<string, string>>): string =>
  Object.entries(values).reduce((text, [name, value]) => text.replace(`{${name}}`, value), t(key));

const dayOf = (iso: string): Date => new Date(`${iso}T00:00:00.000Z`);

function carSentence(piece: DealerDocumentView): readonly string[] {
  const fact = piece.vehicle;
  if (fact === null) return [];
  const lines: string[] = [];
  if (fact.recordedOn !== null) {
    const key =
      fact.provenance === "vehicule"
        ? "dealer.pieces.voitureVehicule"
        : fact.provenance === "emise"
          ? "dealer.pieces.voitureEmise"
          : "dealer.pieces.voitureDeposee";
    lines.push(fillWith(key, { date: formatDayMonth(dayOf(fact.recordedOn)) }));
  }
  if (fact.validUntil !== null)
    lines.push(fillWith("dealer.pieces.valableJusqua", { date: formatDate(dayOf(fact.validUntil)) }));
  return lines;
}

function MonthChips(props: { readonly piece: DealerDocumentView }): ReactElement | null {
  const read = props.piece.readMonths;
  const missing = props.piece.missingMonths;
  if (read === null || missing === null) return null;
  const chips = [
    ...read.map((month) => ({ month, state: "ok" as const })),
    ...missing.map((month) => ({ month, state: "missing" as const })),
  ].sort((a, b) => a.month.localeCompare(b.month));
  return (
    <span className="mt-2 flex flex-wrap gap-1.5">
      {chips.map((chip) => (
        <span
          key={chip.month}
          data-month={chip.month}
          data-state={chip.state}
          className={
            chip.state === "ok"
              ? "inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-0.5 text-xs font-medium text-emerald-700"
              : "inline-flex items-center rounded-full border border-dashed border-gray-300 px-2.5 py-0.5 text-xs font-medium text-gray-500"
          }
        >
          {chip.state === "ok" ? <Check className="h-3 w-3" aria-hidden="true" /> : null}
          {formatMonthName(chip.month)}
        </span>
      ))}
    </span>
  );
}

function DocumentRow(props: {
  readonly piece: DealerDocumentView;
  readonly row: "buyer" | "car";
  readonly next: boolean;
  readonly onPhoto?: ((code: string, file: File) => void) | undefined;
  readonly onFile?: ((code: string, file: File) => void) | undefined;
}): ReactElement {
  const { piece } = props;
  const camera = useRef<HTMLInputElement>(null);
  const chooser = useRef<HTMLInputElement>(null);
  const acts = props.row === "buyer" && !piece.ready;
  const sentences = [
    piece.condition?.label ?? null,
    acts ? piece.helpText : null,
    piece.monthsSentence,
    piece.readingSentence,
    piece.rejectionReason,
    ...(props.row === "car" ? carSentence(piece) : []),
  ].filter((one): one is string => one !== null && one !== "");
  // The icon says the state (designer R, 2 November): the clock while Hakili is reading, the amber triangle on the NEXT missing pièce only, the check once read, the camera for any other pièce still to supply.
  // A49, H's frame 56: EVERY rejected pièce draws the amber triangle and the amber band; among the MISSING ones the triangle is the next one's alone.
  const rejected = piece.status === "rejetee" || piece.reading === "illisible";
  const icon: "clock" | "alert" | "check" | "camera" =
    piece.reading === "en_lecture"
      ? "clock"
      : rejected || props.next
        ? "alert"
        : piece.ready
          ? "check"
          : "camera";
  const banded = acts && (rejected || props.next);
  return (
    <li
      data-testid={`piece-${piece.code}`}
      data-row={props.row}
      data-ready={piece.ready ? "true" : "false"}
      data-band={banded ? "amber" : undefined}
      className={`flex items-start gap-3 border-t border-gray-100 px-5 py-4 first:border-t-0 ${banded ? "bg-amber-50" : ""}`}
    >
      <span
        aria-hidden="true"
        data-icon={icon}
        className={`mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ${
          icon === "check"
            ? "bg-emerald-50 text-emerald-700"
            : icon === "alert"
              ? "bg-amber-100 text-amber-700"
              : "bg-gray-100 text-gray-600"
        }`}
      >
        {icon === "check" ? (
          <Check className="h-5 w-5" />
        ) : icon === "clock" ? (
          <Clock className="h-5 w-5" />
        ) : icon === "alert" ? (
          <AlertTriangle className="h-5 w-5" />
        ) : (
          <Camera className="h-5 w-5" />
        )}
      </span>
      <span className="min-w-0 flex-1">
        <b className="block text-sm font-semibold text-gray-900">{piece.label}</b>
        {sentences.map((sentence) => (
          <small key={sentence} className="mt-0.5 block text-sm text-gray-600">
            {sentence}
          </small>
        ))}
        <MonthChips piece={piece} />
      </span>
      {acts && (props.onPhoto !== undefined || props.onFile !== undefined) ? (
        <span className="flex shrink-0 flex-wrap items-center gap-2">
          {props.onPhoto !== undefined ? (
            <>
              <Button
                variant={props.next ? "primary" : "secondary"}
                size="sm"
                data-testid={`piece-${piece.code}-photo`}
                onClick={() => camera.current?.click()}
              >
                <Camera className="mr-1.5 inline h-4 w-4" aria-hidden="true" />
                {rejected ? t("dealer.pieces.refaire") : t("dealer.pieces.photo")}
              </Button>
              <input
                ref={camera}
                type="file"
                accept="image/*"
                capture="environment"
                className="sr-only"
                tabIndex={-1}
                aria-label={`${t("dealer.pieces.photo")} : ${piece.shortLabel}`}
                data-testid={`piece-${piece.code}-photo-input`}
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  if (file !== undefined) props.onPhoto?.(piece.code, file);
                  event.target.value = "";
                }}
              />
            </>
          ) : null}
          {props.onFile !== undefined ? (
            <>
              <Button
                variant="secondary"
                size="sm"
                data-testid={`piece-${piece.code}-fichier`}
                onClick={() => chooser.current?.click()}
              >
                <Upload className="mr-1.5 inline h-4 w-4" aria-hidden="true" />
                {t("dealer.pieces.fichier")}
              </Button>
              <input
                ref={chooser}
                type="file"
                className="sr-only"
                tabIndex={-1}
                aria-label={`${t("dealer.pieces.fichier")} : ${piece.shortLabel}`}
                data-testid={`piece-${piece.code}-fichier-input`}
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  if (file !== undefined) props.onFile?.(piece.code, file);
                  event.target.value = "";
                }}
              />
            </>
          ) : null}
        </span>
      ) : null}
    </li>
  );
}

export function DocumentsStep(props: {
  readonly answer: DealerDocumentsAnswer;
  /** The assisted file's step names its way on; seated in the dossier's DETAIL (`P1-DLW-052`) the step is read-only and draws no footer (neither is given). */
  readonly continueLabel?: string | undefined;
  readonly onPhoto?: ((code: string, file: File) => void) | undefined;
  readonly onFile?: ((code: string, file: File) => void) | undefined;
  readonly onContinue?: (() => void) | undefined;
  /**
   * `P1-DLW-058` (B5-b, the architect's ruling of 3 November, lane F's component kept and told): **an additive option, ON unless it is turned off.** The pièces step never passes it, so its strip is as it was. The dossier's detail turns it off from SERVED state alone: an offer card or a receipt is on the page (Hakili's face is never beside a money block) or nobody waits on the dossier (`status.waitingOn === null`, the contract's own « closed », R-BATCH-12).
   */
  readonly withStrip?: boolean | undefined;
}): ReactElement {
  const view = documentsViewOf(props.answer);
  const strip = props.withStrip === false ? null : documentsStripOf(props.answer);
  const share = view.bar.expected === 0 ? 0 : Math.min(100, (view.bar.ready / view.bar.expected) * 100);
  return (
    <div className="grid gap-4" data-testid="pieces-step">
      <section className="rounded-2xl border border-gray-200 bg-white px-5 py-4" data-testid="pieces-bar">
        <p className="flex items-baseline gap-2">
          <span className="text-2xl font-semibold text-gray-900">
            {String(view.bar.ready)} {t("dealer.pieces.sur")} {String(view.bar.expected)}
          </span>
          <span className="text-sm text-gray-600">
            {view.bar.expected === 1 ? t("dealer.pieces.prete") : t("dealer.pieces.pretes")}
          </span>
        </p>
        <div
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={view.bar.expected}
          aria-valuenow={view.bar.ready}
          className="mt-2 h-2 overflow-hidden rounded-full bg-gray-100"
        >
          <div className="h-full rounded-full bg-indigo-600" style={{ width: `${String(share)}%` }} />
        </div>
      </section>

      {/* `P1-DLW-053` (designer R, batch 2): ONE strip above the list, Hakili's face, his state word and the concerned pièces' served names; no sentence, no figure. Nothing read, no strip; « il lit » is not served. */}
      {strip === null ? null : (
        <section
          className="flex items-center gap-4 rounded-2xl border border-gray-200 bg-white px-5 py-3"
          data-testid="pieces-hakili"
          data-state={strip.state}
        >
          <HakiliFace state={strip.state} size="strip" />
          <Chip tone={strip.state === "flagging" ? "attention" : "neutre"}>
            {strip.state === "flagging" ? t("dealer.pieces.hakiliSignale") : t("dealer.pieces.hakiliTermine")}
          </Chip>
          <span className="flex flex-wrap gap-x-3 gap-y-1 text-sm font-semibold text-gray-900">
            {strip.names.map((name) => (
              <span key={name} data-name="">
                {name}
              </span>
            ))}
          </span>
        </section>
      )}

      <section className="overflow-hidden rounded-2xl border border-gray-200 bg-white">
        <header className="px-5 pt-4">
          <h2 className="text-base font-semibold text-gray-900">{t("dealer.pieces.titre")}</h2>
          <small className="text-sm text-gray-600">{t("dealer.pieces.sousTitre")}</small>
        </header>
        <ul className="mt-2">
          {view.buyer.map((piece) => (
            <DocumentRow
              key={piece.code}
              piece={piece}
              row="buyer"
              next={piece.code === view.nextMissing}
              onPhoto={props.onPhoto}
              onFile={props.onFile}
            />
          ))}
        </ul>
      </section>

      {view.nextMissing === null || props.onPhoto === undefined ? null : (
        <section
          className="rounded-2xl border border-gray-200 bg-white px-5 py-4"
          data-testid="pieces-astuces"
        >
          <h3 className="text-sm font-semibold text-gray-900">{t("dealer.pieces.astucesTitre")}</h3>
          <ul className="mt-1 list-disc pl-5 text-sm text-gray-700">
            <li>{t("dealer.pieces.astuce1")}</li>
            <li>{t("dealer.pieces.astuce2")}</li>
            <li>{t("dealer.pieces.astuce3")}</li>
          </ul>
        </section>
      )}

      {view.car.length === 0 ? null : (
        <section className="overflow-hidden rounded-2xl border border-gray-200 bg-white">
          <header className="px-5 pt-4">
            <h2 className="text-base font-semibold text-gray-900">{t("dealer.pieces.voitureTitre")}</h2>
            <small className="text-sm text-gray-600">{t("dealer.pieces.voitureSous")}</small>
          </header>
          <ul className="mt-2">
            {view.car.map((piece) => (
              <DocumentRow
                key={piece.code}
                piece={piece}
                row="car"
                next={false}
                onPhoto={props.onPhoto}
                onFile={props.onFile}
              />
            ))}
          </ul>
        </section>
      )}

      {props.continueLabel === undefined || props.onContinue === undefined ? null : (
        <footer className="flex flex-wrap items-center justify-end gap-3 rounded-2xl border border-gray-200 bg-white px-5 py-4">
          {view.allReady ? (
            <GuardedButton variant="primary" testId="pieces-continuer" onClick={props.onContinue}>
              {props.continueLabel}
            </GuardedButton>
          ) : (
            <GuardedButton
              variant="primary"
              disabled
              testId="pieces-continuer"
              reasonDress="chip"
              reasonIcon={<Lock className="h-4 w-4" aria-hidden="true" />}
              reason={
                props.answer.summary.missingSentence ??
                `${String(view.bar.ready)} ${t("dealer.pieces.sur")} ${String(view.bar.expected)}`
              }
            >
              {props.continueLabel}
            </GuardedButton>
          )}
        </footer>
      )}
    </div>
  );
}

/**
 * `P1-DLW-049`: **the step on the real door.** It reads `GET /api/v1/me/dealership/dossiers/{id}/pieces` (`readDocuments`, the session's reader) for the dossier the dealer's own list served, and draws the answer as
 * `DocumentsStep`. A failed read is the shared `ReadFailure` (the door's sentence, « Réessayer »), never a bar of « 0 sur 0 ». Lane E seats this in the dossier's detail through the same four seams.
 */
export function DocumentsStepOfDossier(props: {
  readonly dossierId: string;
  readonly readDocuments: (dossierId: string) => Promise<DoorResult<DealerDocumentsAnswer>>;
  readonly continueLabel?: string | undefined;
  readonly onPhoto?: ((code: string, file: File) => void) | undefined;
  readonly onFile?: ((code: string, file: File) => void) | undefined;
  readonly onContinue?: (() => void) | undefined;
  /** Passed through to `DocumentsStep` (`P1-DLW-058`): on unless turned off. */
  readonly withStrip?: boolean | undefined;
}): ReactElement {
  const { dossierId, readDocuments } = props;
  const [read, setRead] = useState<DoorResult<DealerDocumentsAnswer> | null>(null);
  const load = useCallback(async (): Promise<void> => {
    setRead(await readDocuments(dossierId));
  }, [dossierId, readDocuments]);
  useEffect(() => {
    void load();
  }, [load]);

  if (read === null) return <div data-testid="pieces-chargement" aria-busy="true" />;
  if (!read.ok) {
    return (
      <ReadFailure
        what="dealer.lecture.pieces"
        message={read.message}
        onRetry={() => {
          setRead(null);
          void load();
        }}
        testId="pieces-echec"
        retryTestId="pieces-echec-reessayer"
      />
    );
  }
  return (
    <DocumentsStep
      answer={read.value}
      continueLabel={props.continueLabel}
      onPhoto={props.onPhoto}
      onFile={props.onFile}
      onContinue={props.onContinue}
      withStrip={props.withStrip}
    />
  );
}
